import type { SectionsConfig } from '@backoffice-frontend/authenticated-app-view'
import { NoDataState, PictureSearch } from '@backoffice-frontend/patterns'
import type { Theme } from '@emotion/react'
import { Dialog, Search } from '@moia-dev/pace-web'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCommandPaletteItems } from '../hooks/useCommandPaletteItems'
import { useCommandPaletteSearch } from '../hooks/useCommandPaletteSearch'
import { CommandPaletteFooter } from './CommandPaletteFooter'
import { CommandPaletteResultsList } from './CommandPaletteResultsList'

type CommandPaletteDialogProps = {
  id: string
  isOpen: boolean
  onClose: () => void
  sections: SectionsConfig
  getAreaTranslation: (id: string) => string
}

export const CommandPaletteDialog = ({
  id,
  isOpen,
  onClose,
  sections,
  getAreaTranslation,
}: CommandPaletteDialogProps) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')

  const allItems = useCommandPaletteItems(sections, getAreaTranslation)
  const filteredItems = useCommandPaletteSearch(allItems, searchQuery)

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
    }
  }, [isOpen])

  const handleClose = () => {
    onClose()
  }

  if (!isOpen) return null

  return (
    <Dialog
      id={id}
      onClose={handleClose}
      title={t('commandPalette_dialog_title')}
      caption={t('commandPalette_dialog_caption')}
      size="large"
      css={{
        width: '100%',
        opacity: 1,
        transform: 'scale(1)',
        transition:
          'opacity 180ms cubic-bezier(0.16, 1, 0.3, 1), transform 180ms cubic-bezier(0.16, 1, 0.3, 1)',
        '@starting-style': {
          opacity: 0,
          transform: 'scale(0.97)',
        },
      }}
      footer={<CommandPaletteFooter />}
    >
      <div
        css={(theme: Theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space.Space3,
        })}
      >
        <Search
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={t('commandPalette_search_placeholder')}
          autoFocus
        />

        <div
          css={(theme: Theme) => ({
            height: `calc(${theme.space.Space40} + ${theme.space.Space40})`,
          })}
        >
          {filteredItems.length > 0 ? (
            <CommandPaletteResultsList
              items={filteredItems}
              onClose={handleClose}
            />
          ) : (
            <NoDataState
              title={t('common_noSearchResults_header')}
              description={t('common_spellingCheck_label')}
              picture={<PictureSearch />}
            />
          )}
        </div>
      </div>
    </Dialog>
  )
}
